import { useState, useEffect, useCallback, useRef } from "react";
import { sharedHeld } from "./shell/spoilers";
import type { Conference, WireType } from "./types/nba";
import { isClub } from "./nba/clubs";
import { franchiseOf } from "./otherTeams";
import { noteArriving, noteLeaving, pageTransition } from "./shell/motion";
import { GL_CLUBS, GL_MODE, GL_PREFIX, appHref } from "./league";

/**
 * A ~100-line History API router, in place of pulling in react-router.
 *
 * The app has five routes and no nested layouts, no loaders and no code
 * splitting — the cases a routing library exists to solve. What it does need
 * is that every view be addressable, which is just parse + pushState +
 * popstate.
 *
 * Routes:
 *   /                        -> home: countdown + champion, or summit + standings
 *   /scores                  -> scores, newest game day
 *   /scores/2026-06-13       -> scores, that day
 *   /scores?month=2026-04&team=BOS  -> scores, filtered
 *   /standings               -> standings, East
 *   /standings/west          -> standings, West
 *   /standings/divisions     -> standings by division
 *   /standings/east?season=1980 -> a past season's (closing year: 1980 = 1979-80)
 *   /wire?type=TRADE&team=BOS&page=2
 *   /alltime
 *   /leaders                 -> this season's statistical leaders
 *   /leaders?season=2010     -> a past season's (ESPN year: 2010 = 2009-10)
 *   /draft?year=2026&round=2&team=BOS -> draft order (round up to 21 before 2001)
 *   /game/401859967          -> box score, over the scoreboard
 *   /team/NYK                -> team profile, over whatever section you were on
 *   /team/NYK/schedule       -> that team's whole schedule, as its own page
 *   /player/lebron-james     -> player profile, over either of those
 *   /playoffs                -> the postseason bracket
 *   /playoffs/cup            -> the NBA Cup: groups and knockout bracket
 *   /playoffs?season=2019    -> a past postseason (or Cup, with /cup)
 *   /compare?a=1966&b=3975   -> two players side by side, by ESPN id
 *   /trade?teams=BOS,LAL&p=… -> the Trade Machine, the whole deal in the query
 *
 * nginx already serves index.html for unknown paths (try_files ... /index.html),
 * so a hard refresh on any of these works.
 */

export type DraftViewKey = "picks" | "careers" | "redraft";
/** Which of the injury report's lists is shown (DSPRT-344). */
/** A profile's career table: per game or totals, the regular season or the playoffs. */
export type CareerViewKey = "" | "totals" | "playoffs" | "playoff-totals";
const CAREER_VIEWS: readonly string[] = ["totals", "playoffs", "playoff-totals"];
export type InjuryShow = "all" | "out" | "dtd" | "rest";
/** Which of next summer's free agents are listed (DSPRT-414): all, or one kind. */
export type FreeAgentShow = "all" | "ufa" | "rfa" | "po" | "to";
const FA_SHOWS: readonly string[] = ["ufa", "rfa", "po", "to"];
/** "affiliates" is the G League's only page of its own (DSPRT-474); "wire" is its Moves. */
export type SectionKey = "home" | "scores" | "standings" | "wire" | "alltime" | "leaders" | "draft" | "schedule" | "playoffs" | "compare" | "trade" | "free-agents" | "injuries" | "country" | "affiliates" | "notfound";

export interface Route {
  section: SectionKey;
  /** ISO yyyy-MM-dd, or null for "whatever the newest game day is". */
  date: string | null;
  conf: Conference;
  /** "2026-04", or "" for every month. */
  scoreMonth: string;
  /** Draft year, or null for "whatever the newest one is". */
  draftYear: number | null;
  /** Defaults to the first round. Past the second only for the drafts before 2001, which ran to ten and more (DSPRT-320). */
  draftRound: number;
  /** Team abbreviation, or "" for every team. */
  draftTeam: string;
  /** A birth country as its lowercase ISO code ("fr"), or "" for every country (DSPRT-377). */
  draftCountry: string;
  /** With a country chosen: that class's picks, or every class's ("?years=all"). */
  draftAllYears: boolean;
  /** The school (or club abroad) the Draft is narrowed to, as the feed spells it; "" for every school (DSPRT-388). */
  draftSchool: string;
  /** The pick number the all-years list is narrowed to, or null (DSPRT-388): 1 is every No. 1 pick. */
  draftPick: number | null;
  /** /country/fr: the country whose page is open, as its two letters (DSPRT-389). */
  countryIso: string;
  /** Who was picked, how their careers went, or the class in the order hindsight gives (DSPRT-343, DSPRT-345). */
  draftView: DraftViewKey;
  /** The injury report's list: everyone, or those out, day to day or resting. */
  injuryShow: InjuryShow;
  /** Team abbreviation, or "" for every team. */
  scoreTeam: string;
  wireType: WireType | "ALL";
  wireTeam: string | null;
  wirePage: number;
  /** Box score to show over the current section, or null. */
  gameId: string | null;
  /**
   * The live game full screen over its box score (DSPRT-602): "court", or
   * null for the box score alone. /game/401909093?view=court. Dropped when the
   * game changes or closes.
   */
  gameView: "court" | null;
  /** Team profile to show over the current section, or null. */
  teamAbbr: string | null;
  /**
   * The team whose full schedule page is showing — set only when
   * section === "schedule". Its own page rather than an overlay, unlike
   * teamAbbr: a season's worth of games is a destination in its own right,
   * not a detail floating over whatever you happened to be looking at.
   */
  scheduleTeam: string | null;
  /**
   * The open player profile's URL slug ("lebron-james"), or null.
   *
   * Not an id — see slugify. The route never carries the athlete id itself;
   * PlayerModal gets that from the click that opened it (see openPlayer's
   * `openedPlayer`) or, on a cold load, resolves it from this slug against
   * /players/resolve.
   *
   * Stacks rather than replaces: it is reached by clicking a name inside the
   * box score or the team modal, and closing it must put you back in the table
   * you were reading. So `gameId` and `teamAbbr` are deliberately left set
   * while this is open, and only the path changes.
   */
  playerSlug: string | null;
  /**
   * Which of the open profile's four career tables is up (DSPRT-357): ""
   * for per game in the regular season, the default, which the URL leaves
   * out. /player/lebron-james?career=playoff-totals. Goes back to "" when
   * the profile changes or closes.
   */
  playerCareer: CareerViewKey;
  /** Which competition the playoffs page shows. */
  playoffsView: "bracket" | "cup";
  /** The year it was played in, or null for the latest. */
  playoffsSeason: number | null;
  /** The Cup's games, or its leaders and records (/playoffs/cup?view=leaders, DSPRT-692). */
  cupTab: "games" | "leaders";
  /** The standings' season (closing year, 1980 = 1979-80), or null for the current one (DSPRT-233). */
  standingsSeason: number | null;
  /** The standings by conference, or by division (DSPRT-233). */
  /** A conference, the divisions, or the whole league in one table (/standings/league, DSPRT-407). */
  standingsView: "conference" | "division" | "league";
  /** The Leaders page's season (ESPN year, 2026 = 2025-26), or null for the newest. */
  leadersSeason: number | null;
  /** Leaders' views: the players, the scoring chart (DSPRT-693), this season's players by country, the award races, clutch time. */
  leadersView: "players" | "scoring" | "countries" | "awards" | "clutch";
  /** The country open on /leaders/countries, as its lowercase ISO code, or "" for the list of them (DSPRT-380). */
  leadersCountry: string;
  /** All-Time's tab: the career leaders, the champions, the All-Star Games or the season awards (DSPRT-378, DSPRT-379). */
  allTimeTab: "players" | "champions" | "allstar" | "awards" | "hof";
  /** The compare page's two ESPN athlete ids, either possibly not picked yet. */
  compareA: string | null;
  compareB: string | null;
  /** One season of each side's career in place of the whole of it ("2015-16"), or null for the career (DSPRT-333). */
  compareSeasonA: string | null;
  compareSeasonB: string | null;
  /** Compare's two sides: players, or teams at /compare/teams?a=BOS&b=NYK (DSPRT-300). */
  compareMode: "players" | "teams";
  /** The two team codes, either possibly not picked yet. */
  compareTeamA: string | null;
  compareTeamB: string | null;
  /**
   * The Trade Machine's deal, as its own query string (see trade/model.ts).
   * Kept raw here: the page owns the format, and the router only has to
   * carry it into the address and back.
   */
  trade: string;
  /** The free-agent page's filters: a club code ("" for all 30) and a kind. */
  faTeam: string;
  faShow: FreeAgentShow;
  /**
   * The address of a page that doesn't exist, kept as typed so the address bar
   * doesn't jump to "/" (DSPRT-227) — set only when section === "notfound".
   */
  missing: string | null;
}

const DEFAULT_ROUTE: Route = {
  section: "home",
  date: null,
  conf: "East",
  scoreMonth: "",
  scoreTeam: "",
  draftYear: null,
  draftRound: 1,
  draftTeam: "",
  draftCountry: "",
  draftAllYears: false,
  draftSchool: "",
  draftPick: null,
  countryIso: "",
  draftView: "picks",
  injuryShow: "all",
  wireType: "ALL",
  wireTeam: null,
  wirePage: 1,
  gameId: null,
  gameView: null,
  teamAbbr: null,
  scheduleTeam: null,
  playerSlug: null,
  playerCareer: "",
  playoffsView: "bracket",
  playoffsSeason: null,
  cupTab: "games",
  standingsSeason: null,
  standingsView: "conference",
  leadersSeason: null,
  leadersView: "players",
  leadersCountry: "",
  allTimeTab: "players",
  compareA: null,
  compareB: null,
  compareSeasonA: null,
  compareSeasonB: null,
  compareMode: "players",
  compareTeamA: null,
  compareTeamB: null,
  trade: "",
  faTeam: "",
  faShow: "all",
  missing: null,
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A yyyy-MM-dd that names a day on the calendar: "2026-13-45" has the shape but not the day. */
function realDay(iso: string): boolean {
  if (!ISO_DATE.test(iso)) return false;
  const d = new Date(`${iso}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
}
const MONTH_KEY = /^\d{4}-\d{2}$/;
/** The G League's four kinds of move (DSPRT-474), the NBA's four otherwise. */
const WIRE_TYPES: ReadonlyArray<WireType | "ALL"> = GL_MODE ? ["ALL", "ASSIGN", "RECALL", "TWO_WAY", "CALL_UP"] : ["ALL", "TRADE", "SIGNING", "WAIVE", "INJURY"];

/**
 * A club code from the address, upper-cased, or null when it is no club of
 * this desk's league (B-231). A former name passes: a draft pick and a wire
 * row are both filed under the code the club went by at the time.
 */
function knownClub(raw: string | null | undefined): string | null {
  const code = (raw ?? "").toUpperCase();
  if (!code) return null;
  if (GL_MODE) return GL_CLUBS.has(code) ? code : null;
  return isClub(code) || franchiseOf(code) ? code : null;
}
/** The G League's pages: the NBA desk's minus what the minor league has no data for. */
const GL_SECTIONS: ReadonlySet<string> = new Set(["", "scores", "standings", "wire", "moves", "affiliates", "team", "game", "player"]);

/**
 * A display name as the URL-safe, lowercase-hyphenated slug a player profile
 * opens under — "LeBron James" to "lebron-james". Mirrored server-side as
 * Player.slugify, which is what /players/resolve/{slug} matches against; the
 * two have to agree byte-for-byte or a cold-loaded link would 404 against a
 * roster that actually has the player.
 *
 * Diacritics are FOLDED to their base letter ("Topić" to "topic"), not just
 * dropped as a hyphen boundary — ESPN itself is inconsistent about which
 * form a name arrives in depending on which of its endpoints sent it: the
 * draft feed's athlete.displayName keeps "ć" (as an HTML entity — see
 * EspnText server-side), while the roster feed's already transliterates the
 * same player to plain "c". Without folding, those two endpoints slugify the
 * same person two different ways ("nikola-topi" vs "nikola-topic") and the
 * roster-scan resolver misses someone who is very much on a roster — see the
 * NFD-normalize step, which is what makes both spellings collapse to one.
 */
export function slugify(name: string): string {
  // U+0300-U+036F: Combining Diacritical Marks — what NFD splits a
  // precomposed letter like "ć" into ("c" + a separate combining acute).
  // Written as an escaped range rather than the literal marks themselves,
  // which render invisibly attached to whatever precedes them in source.
  const COMBINING_MARKS = /[\u0300-\u036f]/g;
  return name
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Unknown paths resolve to the default route rather than throwing or 404ing. */
/** A two-letter country code from the address, lowercased, or "" for anything else. */
function iso(v: string | null): string {
  const code = (v ?? "").toLowerCase();
  return /^[a-z]{2}$/.test(code) ? code : "";
}

export function parseRoute(pathname: string, search: string): Route {
  // The G League's addresses sit under /gleague and call the Wire "moves" (DSPRT-474).
  if (GL_MODE) {
    pathname = pathname.replace(/^\/gleague(?=\/|$)/, "") || "/";
    pathname = pathname.replace(/^\/moves(?=\/|$)/, "/wire");
  }
  const seg = pathname.split("/").filter(Boolean);
  const q = new URLSearchParams(search);
  const r: Route = { ...DEFAULT_ROUTE };

  if (GL_MODE && !GL_SECTIONS.has(seg[0] ?? "")) {
    r.section = "notfound";
    r.missing = GL_PREFIX + pathname + search;
    return r;
  }

  switch (seg[0]) {
    case "affiliates":
      // The G League's page only (/gleague/affiliates): on the NBA desk the server answers
      // Page not found, and the app drew the table under the NBA header anyway (B-267).
      if (seg.length > 1 || !GL_MODE) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "affiliates";
      break;

    case "standings": {
      // The G League has two conferences and no divisions, so /gleague/standings/league
      // and /divisions are not its pages: they used to draw the East table under a 404 (B-217).
      if (GL_MODE && ["divisions", "league"].includes(seg[1]?.toLowerCase() ?? "")) {
        r.section = "notfound";
        r.missing = GL_PREFIX + pathname + search;
        break;
      }
      r.section = "standings";
      r.conf = seg[1]?.toLowerCase() === "west" ? "West" : "East";
      r.standingsView = seg[1]?.toLowerCase() === "divisions" ? "division" : seg[1]?.toLowerCase() === "league" ? "league" : "conference";
      const y = Number(q.get("season"));
      // Shape-checked only; which seasons exist is the page's to say.
      r.standingsSeason = Number.isInteger(y) && y > 1946 && y < 2100 ? y : null;
      break;
    }

    case "wire": {
      // Each section's own sub-pages and nothing else (DSPRT-405, #165), as nginx answers them.
      if (seg.length > 1) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "wire";
      const t = (q.get("type") ?? "ALL").toUpperCase() as WireType | "ALL";
      // Validated against the known set: an arbitrary ?type= from a hand-typed
      // URL would otherwise be forwarded to the API as a filter that matches
      // nothing, rendering an empty wire with no explanation.
      if (WIRE_TYPES.includes(t)) r.wireType = t;
      r.wireTeam = knownClub(q.get("team")) ?? null;
      r.wirePage = Math.max(1, Number(q.get("page")) || 1);
      break;
    }

    case "alltime": {
      const tab = seg[1]?.toLowerCase();
      if (seg.length > 2 || (tab && !["champions", "allstar", "awards", "hof"].includes(tab))) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "alltime";
      r.allTimeTab = tab === "champions" || tab === "allstar" || tab === "awards" || tab === "hof" ? tab : "players";
      break;
    }

    case "leaders": {
      // Leaders › Clubs is gone (DSPRT-480): its numbers are all in the league-wide standings.
      if (seg.length === 2 && seg[1].toLowerCase() === "clubs") {
        r.section = "standings";
        r.conf = "East";
        r.standingsView = "league";
        const y = Number(q.get("season"));
        r.standingsSeason = Number.isInteger(y) && y > 1946 && y < 2100 ? y : null;
        break;
      }
      if (seg.length > 2 || (seg[1] && !["scoring", "countries", "awards", "clutch"].includes(seg[1].toLowerCase()))) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "leaders";
      r.leadersView = (["scoring", "countries", "awards", "clutch"].find((v) => v === seg[1]?.toLowerCase()) ?? "players") as Route["leadersView"];
      r.leadersCountry = r.leadersView === "countries" ? iso(q.get("country")) : "";
      const y = Number(q.get("season"));
      r.leadersSeason = Number.isInteger(y) && y > 1946 && y < 2100 ? y : null;
      break;
    }

    case "playoffs":
      if (seg.length > 2 || (seg[1] && seg[1] !== "cup")) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "playoffs";
      r.playoffsView = seg[1] === "cup" ? "cup" : "bracket";
      {
        const y = Number(q.get("season"));
        // Shape-checked only; which years exist is the page's to say.
        r.playoffsSeason = Number.isInteger(y) && y > 1946 && y < 2100 ? y : null;
      }
      r.cupTab = r.playoffsView === "cup" && q.get("view") === "leaders" ? "leaders" : "games";
      break;

    case "compare": {
      if (seg.length > 2 || (seg[1] && seg[1] !== "teams")) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "compare";
      const season = (v: string | null) => (v && /^\d{4}-\d{2}$/.test(v) ? v : null);
      if (seg[1] === "teams") {
        // Shape-checked only; whether the code is a club is the page's to say.
        const code = (v: string | null) => (v && /^[A-Za-z]{2,4}$/.test(v) ? v.toUpperCase() : null);
        r.compareMode = "teams";
        r.compareTeamA = code(q.get("a"));
        r.compareTeamB = code(q.get("b"));
        // A season a side (DSPRT-346): the 1995-96 Bulls against the 2015-16 Warriors.
        r.compareSeasonA = r.compareTeamA ? season(q.get("sa")) : null;
        r.compareSeasonB = r.compareTeamB ? season(q.get("sb")) : null;
        break;
      }
      const id = (v: string | null) => (v && /^\d{1,10}$/.test(v) ? v : null);
      r.compareA = id(q.get("a"));
      r.compareB = id(q.get("b"));
      r.compareSeasonA = r.compareA ? season(q.get("sa")) : null;
      r.compareSeasonB = r.compareB ? season(q.get("sb")) : null;
      break;
    }

    case "trade":
      if (seg.length > 1) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "trade";
      r.trade = search.replace(/^\?/, "");
      break;

    case "free-agents": {
      if (seg.length > 1) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "free-agents";
      const team = (q.get("team") ?? "").toUpperCase();
      r.faTeam = /^[A-Z]{2,4}$/.test(team) ? team : "";
      const show = (q.get("type") ?? "").toLowerCase();
      r.faShow = FA_SHOWS.includes(show) ? (show as FreeAgentShow) : "all";
      break;
    }

    case "injuries": {
      // A page with nothing under it: /injuries/x is not a page.
      if (seg.length > 1) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "injuries";
      const show = q.get("show");
      r.injuryShow = show === "out" || show === "dtd" || show === "rest" ? show : "all";
      break;
    }

    case "country": {
      // /country/fr and nothing under it; which codes have players is the page's to say.
      const code = seg.length === 2 ? iso(seg[1]) : "";
      if (!code) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "country";
      r.countryIso = code;
      break;
    }

    case "draft": {
      // /draft/1997 is the 1997 draft (DSPRT-385, #135), as /scores/2026-10-20 is a day; the
      // canonical pass rewrites it to ?year=. Anything else under /draft is not a page.
      if (seg.length > 1 && !(seg.length === 2 && /^\d{4}$/.test(seg[1]))) { r.section = "notfound"; r.missing = pathname + search; break; }
      r.section = "draft";
      const y = Number(seg[1] ?? q.get("year"));
      // Shape-checked only: which years exist comes from the server, so a
      // year outside the range is left for the page to report rather than
      // silently rewritten to something the user did not ask for.
      // Any four-digit year: /draft/1800 says there's no 1800 draft, as /draft/2030 does (B-106).
      if (Number.isInteger(y) && y >= 1000 && y <= 9999) r.draftYear = y;
      {
        // Shape-checked only: how many rounds a year had is the page's to say.
        const round = Number(q.get("round"));
        r.draftRound = Number.isInteger(round) && round >= 1 && round <= 30 ? round : 1;
      }
      // Checked against the club list, as ?type= above is checked against the
      // wire's: a code that is no club used to be added to the Team menu as
      // though it were one (B-231). A former name counts — a pick is filed
      // under the code the club went by that year.
      r.draftTeam = knownClub(q.get("team")) ?? "";
      r.draftCountry = iso(q.get("country"));
      r.draftSchool = (q.get("school") ?? "").trim().slice(0, 80);
      {
        const pick = Number(q.get("pick"));
        r.draftPick = Number.isInteger(pick) && pick >= 1 && pick <= 300 ? pick : null;
      }
      // Every class at once needs something to narrow it: a country, a school or a pick number.
      r.draftAllYears = q.get("years") === "all" && (!!r.draftCountry || !!r.draftSchool || r.draftPick != null);
      if (!r.draftAllYears) r.draftPick = null;
      {
        const view = q.get("view");
        r.draftView = view === "careers" || view === "redraft" ? view : "picks";
      }
      break;
    }

    case "game":
      // An ESPN id or a Basketball-Reference one (195704130BOS); anything else is no game's address.
      if (seg.length !== 2 || !/^(\d{6,12}|\d{9}[A-Z]{3})$/.test(seg[1])) { r.section = "notfound"; r.missing = pathname + search; break; }
      // The box score renders over the scoreboard, so a direct link to one
      // lands on a populated page rather than a modal floating on nothing.
      r.section = "scores";
      r.gameId = seg[1] ?? null;
      r.gameView = q.get("view") === "court" ? "court" : null;
      break;

    case "team": {
      // A former name's code opens the club that carries it today — /team/SEA
      // is the Thunder (DSPRT-149) — and the canonical-path pass on load
      // rewrites the address to match.
      const abbr = seg[1] ? (GL_MODE ? seg[1].toUpperCase() : franchiseOf(seg[1]) ?? seg[1].toUpperCase()) : null;
      // Only /team/X and /team/X/schedule are pages; the server answers 404 for
      // anything else, and the page says so too, not the club (B-153).
      if (seg.length > 3 || (seg[2] !== undefined && seg[2] !== "schedule")) { r.section = "notfound"; r.missing = pathname + search; break; }
      if (seg[2] === "schedule") {
        r.section = "schedule";
        r.scheduleTeam = abbr;
      } else {
        // Standings rather than scores: a team profile opened cold reads
        // better over the table it belongs in than over a single night's
        // slate.
        r.section = "standings";
        r.teamAbbr = abbr;
      }
      break;
    }

    case "player":
      if (seg.length !== 2) { r.section = "notfound"; r.missing = pathname + search; break; }
      // Opened cold there is no box score or roster to sit on, so the profile
      // stands alone over the default section. Reached the normal way — from a
      // name inside one of those two — navigate() merges into the live route
      // and both stay open underneath.
      r.playerSlug = seg[1] ?? null;
      r.playerCareer = CAREER_VIEWS.includes(q.get("career") ?? "") ? (q.get("career") as CareerViewKey) : "";
      break;

    case undefined:
      // "/" is the home page: the countdown and champion out of season, last
      // night's summit and the standings in it. See HomeView.
      r.section = "home";
      break;

    case "scores":
      r.section = "scores";
      if (seg[1] && realDay(seg[1])) r.date = seg[1];
      // Shape-checked, not membership-checked: which months and teams exist
      // depends on data this module cannot see, so a filter that matches
      // nothing is left to the page to report rather than silently dropped.
      if (MONTH_KEY.test(q.get("month") ?? "")) r.scoreMonth = q.get("month") as string;
      r.scoreTeam = (q.get("team") ?? "").toUpperCase();
      break;

    default:
      // An unknown path says so (DSPRT-227) rather than landing on the home
      // page with the address rewritten to "/" and no word of why.
      r.section = "notfound";
      r.missing = pathname + search;
      break;
  }

  return r;
}

export function routeToPath(r: Route): string {
  // Spelled for the league shown: under /gleague, with the Wire as /moves (DSPRT-474).
  return appHref(innerPath(r));
}

function innerPath(r: Route): string {
  // Overlays own the URL while open, innermost first: a player profile opened
  // from a box score leaves gameId set underneath it, and the path has to name
  // the thing actually on top. Closing it drops back through the same ladder
  // to the section path.
  if (r.playerSlug) return `/player/${r.playerSlug}${r.playerCareer ? `?career=${r.playerCareer}` : ""}`;
  if (r.teamAbbr) return `/team/${r.teamAbbr}`;
  // A shared game keeps its ?hide=1 until the score is shown (B-023), so a reload or a forwarded link stays spoiler-free.
  if (r.gameId) {
    const q = [sharedHeld(r.gameId) ? "hide=1" : "", r.gameView ? `view=${r.gameView}` : ""].filter(Boolean).join("&");
    return `/game/${r.gameId}${q ? `?${q}` : ""}`;
  }

  switch (r.section) {
    case "home":
      return "/";

    case "notfound":
      return r.missing ?? "/";

    case "standings": {
      const base = r.standingsView === "division" ? "/standings/divisions" : r.standingsView === "league" ? "/standings/league" : `/standings/${r.conf.toLowerCase()}`;
      return r.standingsSeason ? `${base}?season=${r.standingsSeason}` : base;
    }

    case "wire": {
      const q = new URLSearchParams();
      if (r.wireType !== "ALL") q.set("type", r.wireType);
      if (r.wireTeam) q.set("team", r.wireTeam);
      if (r.wirePage > 1) q.set("page", String(r.wirePage));
      const s = q.toString();
      return s ? `/wire?${s}` : "/wire";
    }

    case "country":
      return `/country/${r.countryIso}`;

    case "affiliates":
      return "/affiliates";

    case "alltime":
      return r.allTimeTab === "players" ? "/alltime" : `/alltime/${r.allTimeTab}`;

    case "leaders":
      if (r.leadersView === "countries") return `/leaders/countries${r.leadersCountry ? `?country=${r.leadersCountry}` : ""}`;
      // This season's races and clutch time (DSPRT-452): no season to step through.
      if (r.leadersView === "awards" || r.leadersView === "clutch") return `/leaders/${r.leadersView}`;
      // The scoring chart steps through seasons as the players' boards do (DSPRT-693).
      if (r.leadersView === "scoring") return `/leaders/scoring${r.leadersSeason ? `?season=${r.leadersSeason}` : ""}`;
      return `/leaders${r.leadersSeason ? `?season=${r.leadersSeason}` : ""}`;

    case "playoffs": {
      const base = r.playoffsView === "cup" ? "/playoffs/cup" : "/playoffs";
      const q = new URLSearchParams();
      if (r.playoffsSeason) q.set("season", String(r.playoffsSeason));
      if (r.playoffsView === "cup" && r.cupTab === "leaders") q.set("view", "leaders");
      const qs = q.toString();
      return qs ? `${base}?${qs}` : base;
    }

    case "compare": {
      const q = new URLSearchParams();
      const teams = r.compareMode === "teams";
      const [a, b] = teams ? [r.compareTeamA, r.compareTeamB] : [r.compareA, r.compareB];
      if (a) q.set("a", a);
      if (b) q.set("b", b);
      if (a && r.compareSeasonA) q.set("sa", r.compareSeasonA);
      if (b && r.compareSeasonB) q.set("sb", r.compareSeasonB);
      const s3 = q.toString();
      const base = teams ? "/compare/teams" : "/compare";
      return s3 ? `${base}?${s3}` : base;
    }

    case "schedule":
      return `/team/${r.scheduleTeam}/schedule`;

    case "trade":
      return r.trade ? `/trade?${r.trade}` : "/trade";

    case "free-agents": {
      const q = new URLSearchParams();
      if (r.faTeam) q.set("team", r.faTeam.toLowerCase());
      if (r.faShow !== "all") q.set("type", r.faShow);
      const s2 = q.toString();
      return s2 ? `/free-agents?${s2}` : "/free-agents";
    }

    case "injuries":
      return r.injuryShow === "all" ? "/injuries" : `/injuries?show=${r.injuryShow}`;

    case "draft": {
      const q = new URLSearchParams();
      if (r.draftYear != null) q.set("year", String(r.draftYear));
      if (r.draftRound > 1) q.set("round", String(r.draftRound));
      if (r.draftTeam) q.set("team", r.draftTeam);
      if (r.draftCountry) q.set("country", r.draftCountry);
      if (r.draftSchool) q.set("school", r.draftSchool);
      if (r.draftAllYears && r.draftPick != null) q.set("pick", String(r.draftPick));
      if (r.draftAllYears && (r.draftCountry || r.draftSchool || r.draftPick != null)) q.set("years", "all");
      if (r.draftView !== "picks") q.set("view", r.draftView);
      const s2 = q.toString();
      return s2 ? `/draft?${s2}` : "/draft";
    }

    case "scores":
    default: {
      const q = new URLSearchParams();
      if (r.scoreMonth) q.set("month", r.scoreMonth);
      if (r.scoreTeam) q.set("team", r.scoreTeam);
      const s = q.toString();
      const path = r.date ? `/scores/${r.date}` : "/scores";
      return s ? `${path}?${s}` : path;
    }
  }
}

/**
 * Left-to-right order of the pages, as the nav shows them, so a page change
 * can slide in from the side it's on (DSPRT-203). A page not in the nav (a
 * team's schedule) just fades.
 */
const PAGE_ORDER: SectionKey[] = GL_MODE ? ["home", "scores", "standings", "wire", "affiliates"] : ["home", "scores", "standings", "leaders", "wire", "injuries", "playoffs", "draft", "compare", "trade", "free-agents", "alltime"];
function slideDirection(from: Route, to: Route): number {
  const a = PAGE_ORDER.indexOf(from.section), b = PAGE_ORDER.indexOf(to.section);
  return a < 0 || b < 0 ? 0 : Math.sign(b - a);
}

/** The page under any overlay: what a scroll position belongs to. */
function pageOf(r: Route): string {
  return r.section === "schedule" ? `schedule:${r.scheduleTeam}` : r.section;
}

export interface Navigate {
  (patch: Partial<Route>, opts?: { replace?: boolean }): void;
}

/**
 * Opens an address of the site from a component that has no navigate to
 * call (a flag beside a name, DSPRT-389): the address goes on the history
 * and the router reads it as it reads Back and Forward.
 */
export function openPath(href: string): void {
  if (href === window.location.pathname + window.location.search) return;
  window.history.pushState(null, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}

export function useRoute(): [Route, Navigate] {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );

  // Rewrites the address bar to the canonical form of whatever it parsed to,
  // once, on load. Without it an unknown path such as /nonsense rendered the
  // Scores page while the URL still said /nonsense (and the canonical tag
  // repeated it), and /standings never became /standings/east.
  // replaceState, not pushState: a correction should not cost a Back press.
  useEffect(() => {
    const canonical = routeToPath(parseRoute(window.location.pathname, window.location.search));
    if (canonical !== window.location.pathname + window.location.search) {
      // The #anchor stays: it names a place on the page (/team/bkn#picks, /about#privacy), not the page.
      window.history.replaceState(null, "", canonical + window.location.hash);
    }
  }, []);

  // The route as of the last render, so navigate can tell a new page from an
  // overlay or a filter before it commits (DSPRT-201).
  const current = useRef(route);
  current.current = route;

  useEffect(() => {
    const onPop = () => {
      // An entry this router pushed carries the route it showed (B-034): a box
      // score over Standings comes back over Standings, not over Scores, which
      // is all its /game/ address says.
      const saved = (window.history.state as HistoryEntry | null)?.route;
      const here = window.location.pathname + window.location.search;
      const next = saved && routeToPath(saved) === here ? saved : parseRoute(window.location.pathname, window.location.search);
      const apply = () => setRoute(next);
      if (pageOf(current.current) !== pageOf(next)) {
        pageTransition(apply, slideDirection(current.current, next));
        // Back to a page left from one of its rows: that row again (DSPRT-611).
        noteArriving();
      } else apply();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Another part of the page (the score strip) opening an address of this
  // desk: handled here so the page under a box score stays the page you were
  // on (B-034). The event is cancelled to say it was taken.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const href = (e as CustomEvent<string>).detail;
      const url = new URL(href, window.location.origin);
      const r = parseRoute(url.pathname, url.search);
      if (r.section === "notfound") return;
      e.preventDefault();
      if (r.gameId) navigateRef.current({ gameId: r.gameId, teamAbbr: null, playerSlug: null });
      else navigateRef.current(r);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const navigate = useCallback<Navigate>((patch, opts) => {
    // Closing a box score, team or profile that was opened over this page
    // steps back to the entry beneath it (B-036). Pushing the page's address
    // again left the dialog one Back press away, and Back reopened it.
    const here = window.history.state as HistoryEntry | null;
    if (here?.opened && !opts?.replace && closesOverlayOnly(current.current, patch)) {
      window.history.back();
      return;
    }
    // Closing two at once (a game from its super-view): two steps back, when
    // both were opened over this page, so neither is left a Back press away.
    if (here?.opened && here.under && !opts?.replace && closesTopTwo(current.current, patch)) {
      window.history.go(-2);
      return;
    }
    // A move to another page fades and rises in a View Transition; an overlay
    // opening, a filter or a correction changes in place, as before.
    const target = { ...current.current, ...patch };
    const newPage = !opts?.replace && pageOf(current.current) !== pageOf(target);
    if (newPage) noteLeaving();
    const apply = () => setRoute((prev) => {
      const next = { ...prev, ...patch };
      // Leaving the draft for another page forgets which draft was open, so
      // coming back lands on the latest one's first round (DSPRT-148). A
      // profile opened over the draft isn't leaving it — that keeps section.
      if (prev.section === "draft" && next.section !== "draft") {
        next.draftYear = null;
        next.draftRound = 1;
        next.draftTeam = "";
        next.draftCountry = "";
        next.draftSchool = "";
        next.draftPick = null;
        next.draftAllYears = false;
        next.draftView = "picks";
      }
      // Another profile, or none, opens on its per-game table: the view in
      // the URL belongs to the profile it was chosen on (DSPRT-357).
      if (prev.playerSlug !== next.playerSlug && patch.playerCareer === undefined) next.playerCareer = "";
      // Another game, or none, opens on its box score (DSPRT-602).
      if (prev.gameId !== next.gameId && patch.gameView === undefined) next.gameView = null;
      // The same for the Leaders page's season (DSPRT-151).
      if (prev.section === "leaders" && next.section !== "leaders") { next.leadersSeason = null; next.leadersView = "players"; next.leadersCountry = ""; }
      if (prev.section === "alltime" && next.section !== "alltime") next.allTimeTab = "players";
      // The Cup's Leaders tab belongs to the Cup page (DSPRT-692).
      if ((next.section !== "playoffs" || next.playoffsView !== "cup") && patch.cupTab === undefined) next.cupTab = "games";
      if (prev.section === "free-agents" && next.section !== "free-agents" && patch.faTeam === undefined) { next.faTeam = ""; next.faShow = "all"; }
      const path = routeToPath(next);
      if (path !== window.location.pathname + window.location.search) {
        // replace for corrections that shouldn't cost a Back press — resolving
        // an unknown path to "/", or clamping an out-of-range wire page.
        // An overlay opened over the same page is marked, so closing it can go back.
        const opened = !opts?.replace && pageOf(prev) === pageOf(next) && overlays(next) > overlays(prev);
        const entry: HistoryEntry = { route: next, opened, under: opened && !!(window.history.state as HistoryEntry | null)?.opened };
        if (opts?.replace) window.history.replaceState({ ...entry, opened: (window.history.state as HistoryEntry | null)?.opened }, "", path);
        else window.history.pushState(entry, "", path);
      }
      // A new page opens at its top (DSPRT-181): the tab bar used to keep the
      // old page's scroll, so Leaders opened at its footer. Overlays (a box
      // score, a team, a player) and a filter on the same page keep their
      // place, and Back/Forward never come through here — the browser
      // restores those.
      if (!opts?.replace && pageOf(prev) !== pageOf(next)) window.scrollTo(0, 0);
      return next;
    });
    if (newPage) pageTransition(apply, slideDirection(current.current, target));
    else apply();
  }, []);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  return [route, navigate];
}

/** What this router keeps on each history entry it writes. */
interface HistoryEntry {
  route: Route;
  /** A box score, team or profile opened over the page of the entry before. */
  opened?: boolean;
  /** The entry before was itself one opened over the page: two steps back is the page. */
  under?: boolean;
}

/** The event the score strip sends to open an address in the NBA desk (B-034). */
export const OPEN_EVENT = "statsummit:open";

/** How many dialogs are stacked on the page. */
function overlays(r: Route): number {
  return (r.gameId ? 1 : 0) + (r.gameId && r.gameView ? 1 : 0) + (r.teamAbbr ? 1 : 0) + (r.playerSlug ? 1 : 0);
}

type OverlayKey = "playerSlug" | "teamAbbr" | "gameView" | "gameId";
/** The open dialogs, top first. */
function overlayLadder(r: Route): OverlayKey[] {
  const out: OverlayKey[] = [];
  if (r.playerSlug) out.push("playerSlug");
  if (r.teamAbbr) out.push("teamAbbr");
  if (r.gameId && r.gameView) out.push("gameView");
  if (r.gameId) out.push("gameId");
  return out;
}
const OVERLAY_KEYS: (keyof Route)[] = ["gameId", "gameView", "teamAbbr", "playerSlug", "playerCareer"];

/** Does this patch close exactly the top `n` dialogs, leaving the page and the rest as they are? */
function closesTop(r: Route, patch: Partial<Route>, n: number): boolean {
  const keys = Object.keys(patch) as (keyof Route)[];
  if (!keys.length || !keys.every((k) => OVERLAY_KEYS.includes(k))) return false;
  const top = overlayLadder(r).slice(0, n);
  if (top.length < n || top.some((k) => patch[k] !== null)) return false;
  return keys.every((k) => (top as (keyof Route)[]).includes(k) || k === "playerCareer" || patch[k] === r[k]);
}
/** Does this patch only close the top dialog, leaving the page and the rest as they are? */
const closesOverlayOnly = (r: Route, patch: Partial<Route>) => closesTop(r, patch, 1);
const closesTopTwo = (r: Route, patch: Partial<Route>) => closesTop(r, patch, 2);

/* ------------------------------------------------------------------ *
 * Document metadata
 * ------------------------------------------------------------------ */

function setMeta(selector: string, content: string): void {
  const el = document.head.querySelector<HTMLMetaElement>(selector);
  if (el) el.setAttribute("content", content);
}

/**
 * Keeps <title> and the OG/Twitter tags in step with the route.
 *
 * This does not make the app crawlable — the HTML shipped from nginx is still
 * an empty shell, so anything that doesn't run JS sees only the static
 * defaults in index.html. It does fix the tab title, the browser history
 * entries, and any scraper that does execute scripts. Real crawlable previews
 * need prerendering, which is a deploy-side change.
 */
/**
 * One address per standings table (DSPRT-398, #160), as page-head names it:
 * /standings is the East table, and before 1970-71 every address shows the
 * divisions. Any other path comes back as it is.
 */
function standingsCanonical(path: string, season: number | null): string {
  const m = /^\/standings(?:\/([^/]+))?$/i.exec(path);
  if (!m) return path;
  if (season != null && season < 1971) return "/standings/divisions";
  const sub = (m[1] ?? "east").toLowerCase();
  // /standings/league is a page of its own (DSPRT-407): it used to name the East
  // page as its canonical, so a table of all thirty clubs told search engines it
  // was really the Eastern Conference (B-206).
  return sub === "west" || sub === "divisions" || sub === "league" ? `/standings/${sub}` : "/standings/east";
}

export function syncMeta(title: string, description: string): void {
  document.title = title;
  setMeta('meta[name="description"]', description);
  setMeta('meta[property="og:title"]', title);
  setMeta('meta[property="og:description"]', description);
  setMeta('meta[name="twitter:title"]', title);
  setMeta('meta[name="twitter:description"]', description);

  // The canonical address of this view, on the production domain whatever host
  // it was actually opened on. Path only: the query string carries filters
  // that make the same page reachable many ways — bar a past season's
  // standings, leaders or bracket, a different table under the same path
  // (DSPRT-237; page-head keeps it the same way).
  const query = new URLSearchParams(location.search);
  const season = query.get("season");
  const keep = season && /^\d{4}$/.test(season) && /^\/(standings|leaders|playoffs)(\/|$)/.test(location.pathname);
  // A draft year is its own page as well (DSPRT-316).
  const year = query.get("year");
  const onDraft = /^\/draft\/?$/.test(location.pathname);
  const keepYear = year && /^\d{4}$/.test(year) && onDraft;
  // Careers and Redraft are pages of their own too (DSPRT-398, #161).
  const view = query.get("view");
  const keepView = onDraft && (view === "careers" || view === "redraft") ? view : null;
  const draftQuery = [keepYear ? `year=${year}` : "", keepView ? `view=${keepView}` : ""].filter(Boolean).join("&");
  // The Cup's leaders are a page of their own as well (DSPRT-692).
  const cupLeaders = /^\/playoffs\/cup\/?$/.test(location.pathname) && view === "leaders";
  const pageQuery = [keep ? `season=${season}` : draftQuery, cupLeaders ? "view=leaders" : ""].filter(Boolean).join("&");
  const url = `https://statsummit.com${standingsCanonical(location.pathname.replace(/\/+$/, "") || "/", keep ? Number(season) : null)}${pageQuery ? `?${pageQuery}` : ""}`;
  setMeta('meta[property="og:url"]', url);
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);

  // The Wire announces its feed, one club's when the page is filtered to it
  // (DSPRT-316, #92). page-head writes the same tag on a cold load; this
  // keeps it true as the app moves between pages.
  const team = query.get("team");
  const onWire = /^\/wire\/?$/.test(location.pathname);
  let feed = document.head.querySelector<HTMLLinkElement>('link[rel="alternate"][type="application/rss+xml"]');
  if (!onWire) {
    feed?.remove();
  } else {
    if (!feed) {
      feed = document.createElement("link");
      feed.rel = "alternate";
      feed.type = "application/rss+xml";
      document.head.appendChild(feed);
    }
    const code = team && /^[A-Za-z]{2,4}$/.test(team) ? team.toUpperCase() : null;
    feed.title = "The Wire · StatSummit";
    feed.href = code ? `https://statsummit.com/wire/${code}.rss` : "https://statsummit.com/wire.rss";
  }
}
